import { tagged } from "@mirage/service-logging";
import * as scoring from "@mirage/service-typeahead-search/service/scoring";
import { TYPEAHEAD_SEARCH_SOURCES } from "@mirage/service-typeahead-search/service/sources";
import { typeahead } from "@mirage/service-typeahead-search/service/types";
import * as rx from "rxjs";
import * as op from "rxjs/operators";

import type { TypeaheadCache } from "@mirage/service-typeahead-search/service/typeahead-cache";
import type { ExtractTypePredicate } from "@mirage/shared/util/ts-util-types";
import type { Observable } from "rxjs";
import { predicateFactory } from "../utils/predicate-factory";
import {
  clampSuggestedQueryMaxPosition,
  dedupeResultsBasedOnURLAndScore,
  isAskUserQuery,
  isRecentQuery,
  isSuggestedQuery,
  isURLShortcut,
  isWriteUserQuery,
  sortScoredResults,
} from "./utils";
import ResultType = typeahead.ResultType;

export const PIPELINE_DEBUG = false;
PIPELINE_DEBUG satisfies false;

const logger = tagged("service-typeahead-search/search/pipeline");

export function tapLogger<T>(name: string) {
  return op.tap((results: T) => {
    if (!PIPELINE_DEBUG) return;
    logger.debug(`OMNIBOX_SEARCH: ${name}:`, results);
  });
}

/**
 * Gets sources based on sections array and returns an observable of results
 */
export function getSources(
  query: string,
  mode: typeahead.Mode,
  options: typeahead.TaggedSearchOptions,
  auxiliarySources: typeahead.PredicateSubject<typeahead.SourceId>[],
  cacheAnalysis: typeahead.CacheAnalysis,
): Observable<typeahead.ScoredResult> {
  const callableSources = predicateFactory<typeahead.SourceId>(
    query,
    mode,
    [...TYPEAHEAD_SEARCH_SOURCES, ...auxiliarySources],
    {
      config: options.config,
      cacheAnalysis,
      sections: options.sections,
    },
  );

  return rx.from(callableSources).pipe(
    op.mergeMap((source) => runner(source, query, mode, options)),
    // tapLogger<typeahead.ScoredResult>('getSources'),
  );
}

export function runner(
  source: typeahead.PredicateSubject<typeahead.SourceId>,
  query: string,
  mode: typeahead.Mode,
  options: typeahead.TaggedSearchOptions,
): Observable<typeahead.ScoredResult> {
  // We can reduce this to the maximum number emitted from any section/source
  // This number is 8 currently, but we can use 15 to be safe, this is negligable
  const WARN_TOO_MANY_RESULTS_THRESHOLD = 15;
  const perSourceUUIDCountSet = new Set<string>();
  let warned = false;

  return (
    source
      .search(query, mode, options)
      .pipe(op.subscribeOn(rx.asyncScheduler))
      // swallow source-level failures
      .pipe(
        op.catchError((e) => {
          logger.error(`search source error`, e);
          return rx.EMPTY;
        }),
      )
      .pipe(
        op.tap((item) => {
          perSourceUUIDCountSet.add(item?.uuid);

          if (
            !warned &&
            perSourceUUIDCountSet.size > WARN_TOO_MANY_RESULTS_THRESHOLD
          ) {
            warned = true;
            logger.warn(
              `More than ${WARN_TOO_MANY_RESULTS_THRESHOLD} items emitted from the source ${source.id}!`,
            );
          }
        }),
      )
    // uncomment this line to add some delay for easier development
    // .pipe(rx.delay(500))
  );
}

export function filterDropboxersOnlyResults(config: typeahead.Config) {
  return (
    source: Observable<typeahead.ScoredResult>,
  ): Observable<typeahead.ScoredResult> => {
    return source.pipe(
      op.filter(
        (result) =>
          result.type !== ResultType.URLShortcut ||
          !result.result.dropboxersOnly ||
          (result.result.dropboxersOnly && config.isDropboxer),
      ),
      // tapLogger<typeahead.ScoredResult>('filterDropboxersOnlyResults'),
    );
  };
}

export function dedupeResults() {
  return (
    source: Observable<typeahead.ScoredResult>,
  ): Observable<typeahead.ScoredResult[]> => {
    return source.pipe(
      rx.distinct(({ uuid }) => uuid),
      op.scan((acc, value) => {
        return [...acc, value];
      }, [] as typeahead.ScoredResult[]),
      op.map(dedupeResultsBasedOnURLAndScore),
      // tapLogger<typeahead.ScoredResult[]>('dedupeResults'),
    );
  };
}

export function dedupeRecentsInDefaultMode(mode: typeahead.Mode) {
  return (
    source: Observable<typeahead.ScoredResult[]>,
  ): Observable<typeahead.ScoredResult[]> => {
    return source.pipe(
      op.map((results) => {
        if (mode !== typeahead.Mode.Default) return results;

        const contentSeen = new Set<string>();

        return results.filter((result) => {
          if (result.type === typeahead.ResultType.RecentChat) {
            const key = result.result.title;
            if (contentSeen.has(key)) return false;
            contentSeen.add(key);
            return true;
          }

          if (result.type === typeahead.ResultType.PreviousQuery) {
            const key = result.result.query;
            if (contentSeen.has(key)) return false;
            contentSeen.add(key);
            return true;
          }

          return true;
        });
      }),
      // tapLogger<typeahead.ScoredResult[]>('dedupeRecentsInDefaultMode'),
    );
  };
}

export function sortResultsBySection(isOmniboxEnabled: boolean) {
  return (
    source: Observable<typeahead.ScoredResult[]>,
  ): Observable<typeahead.ScoredResult[]> => {
    return source.pipe(
      op.map((results) =>
        [...results].sort(sortScoredResults(isOmniboxEnabled)),
      ),
      // tapLogger<typeahead.ScoredResult[]>('sortResultsBySection'),
    );
  };
}

export function limitAndSortBySection(
  grossLimit: number,
  sectionLimit: number | undefined,
  mode: typeahead.Mode,
  config: typeahead.Config,
) {
  return (
    source: Observable<typeahead.ScoredResult[]>,
  ): Observable<typeahead.ScoredResult[]> => {
    return source.pipe(
      op.map((sourceResults) => {
        if (!config.isOmniboxEnabled) return sourceResults;

        const suggestedQueries = sourceResults.filter(isSuggestedQuery);
        const askUserQueries = sourceResults.filter(isAskUserQuery);
        const writeUserQueries = sourceResults.filter(isWriteUserQuery);
        const priorityResults = [
          ...suggestedQueries,
          ...askUserQueries,
          ...writeUserQueries,
        ];

        const regularResults = sourceResults.filter(
          (result) =>
            !isSuggestedQuery(result) &&
            !isAskUserQuery(result) &&
            !isWriteUserQuery(result),
        );

        // Group regular results by section
        const sectionMap = regularResults.reduce(
          (acc, result) => {
            const sectionId = result.sectionId || "unknown";
            if (!acc[sectionId]) acc[sectionId] = [];
            acc[sectionId].push(result);
            return acc;
          },
          {} as Record<string, typeahead.ScoredResult[]>,
        );

        const getDynamicSectionLimit = () => {
          if (
            sectionLimit &&
            priorityResults.length &&
            [
              typeahead.Mode.Ask,
              typeahead.Mode.Find,
              typeahead.Mode.Default,
              typeahead.Mode.Write,
            ].includes(mode)
          ) {
            return sectionLimit - 1;
          }

          return sectionLimit;
        };

        // Apply per-section limit and flatten back to array
        // This maintains the original score ordering within each section
        const limitedBySectionResults = Object.values(sectionMap)
          .map((sectionResults) =>
            sectionResults.slice(0, getDynamicSectionLimit()),
          )
          .flat();

        // Re-sort all section-limited results by score
        const sortedLimitedBySectionResults = limitedBySectionResults.sort(
          sortScoredResults(false),
        );

        const remainingLimit = grossLimit - priorityResults.length;
        const limitedSectionResults = sortedLimitedBySectionResults.slice(
          0,
          remainingLimit,
        );

        // Combine priority results with limited section results
        const combinedResults = [...priorityResults, ...limitedSectionResults];
        return combinedResults.slice(0, grossLimit);
      }),
      // tapLogger<typeahead.ScoredResult[]>('limitAndSortBySection'),
    );
  };
}

export function finalizeScoredSources(
  query: string,
  mode: typeahead.Mode,
  callableSections: typeahead.PredicateSubject<typeahead.SectionId>[],
  cache: TypeaheadCache,
  config: typeahead.Config,
) {
  return (
    source: Observable<typeahead.ScoredResult[]>,
  ): Observable<typeahead.ScoredResult[]> => {
    return source.pipe(
      op.switchMap((sourceResults) => {
        if (!config.isOmniboxEnabled) {
          return rx.of(sourceResults);
        }

        return rx
          .from(
            scoring.sectionScore(
              query,
              mode,
              sourceResults,
              callableSections,
              cache,
            ),
          )
          .pipe(
            op.map((sectionScores) => {
              return sourceResults.map((sourceResult) => ({
                ...sourceResult,
                sectionScore: sectionScores[sourceResult.sectionId!],
              }));
            }),
          );
      }),
      // tapLogger<typeahead.ScoredResult[]>('finalizeScoredSources'),
    );
  };
}

/**
 * Organizes results by type and applies positioning rules for suggested queries
 * TODO: In the coming performance branches this function will be significantly changed (04/30/2025)
 */
export function clampSection(
  sectionedSortedResults: typeahead.ScoredResult[],
  query: string,
  grossLimit: number,
  config: typeahead.Config,
) {
  if (!config.isOmniboxEnabled) {
    // Non-Omnibox flow - keep existing implementation

    // suggested-search
    const suggestedQuery = sectionedSortedResults.filter(
      isSuggestedQuery,
    )?.[0] as
      | ExtractTypePredicate<typeof isSuggestedQuery>
      // this can be undefined in practice, so this just forces the type to match reality
      | undefined;

    // shortcuts
    const shortcuts = sectionedSortedResults.filter(isURLShortcut);

    // recent queries
    const recentQueries = sectionedSortedResults
      .filter(isRecentQuery)
      .slice(0, query.length > 0 ? 1 : grossLimit);

    // all other sources
    const rest = sectionedSortedResults.filter(
      (result) =>
        !isSuggestedQuery(result) &&
        !isURLShortcut(result) &&
        !isRecentQuery(result),
    );

    // combine all but the suggested-search
    const combinedAndLimited = [...shortcuts, ...recentQueries, ...rest]
      .slice(0, grossLimit)
      // sort by score again to ensure they are all in the correct order post merge
      .sort(sortScoredResults(config.isOmniboxEnabled));

    // then clamp the suggested query to the first or second position depending on it's score
    // WARNING, THE RESULTS CANNOT BE SORTED AGAIN AFTER THIS POINT!
    return clampSuggestedQueryMaxPosition(suggestedQuery, combinedAndLimited);
  }

  // Omnibox flow - handle section-based results

  // For Omnibox, we've already prioritized SuggestedQuery and AskUserQuery in limitedAndSortedSources$
  // This function just ensures they're positioned correctly within the section
  const suggestedQuery = sectionedSortedResults.filter(
    isSuggestedQuery,
  )?.[0] as ExtractTypePredicate<typeof isSuggestedQuery> | undefined;

  const askUserQuery = sectionedSortedResults.filter(isAskUserQuery)?.[0] as
    | ExtractTypePredicate<typeof isAskUserQuery>
    | undefined;

  const writeUserQuery = sectionedSortedResults.filter(
    isWriteUserQuery,
  )?.[0] as ExtractTypePredicate<typeof isWriteUserQuery> | undefined;

  if (!suggestedQuery && !askUserQuery && !writeUserQuery) {
    return sectionedSortedResults;
  }

  const otherResults = sectionedSortedResults.filter(
    (result) =>
      !isSuggestedQuery(result) &&
      !isAskUserQuery(result) &&
      !isWriteUserQuery(result),
  );

  if (suggestedQuery) {
    return [suggestedQuery, ...otherResults];
  }

  if (writeUserQuery) {
    return [writeUserQuery, ...otherResults];
  }

  if (askUserQuery) {
    return [askUserQuery, ...otherResults];
  }

  return otherResults;
}

/**
 * Processes sections with proper organization and clamping
 */
export function postProcessResults(
  query: string,
  grossLimit: number,
  config: typeahead.Config,
) {
  return (
    source: Observable<typeahead.ScoredResult[]>,
  ): Observable<typeahead.ScoredResult[]> => {
    return source.pipe(
      op.map((sortedResults) => {
        if (!config.isOmniboxEnabled) {
          return clampSection(sortedResults, query, grossLimit, config);
        }

        return sortedResults
          .reduce((acc, sortedResult) => {
            const existingSectionedSortedResults = acc.find((section) =>
              section.find((r) => r.sectionId === sortedResult.sectionId),
            );

            if (existingSectionedSortedResults) {
              existingSectionedSortedResults.push(sortedResult);
            } else {
              acc.push([sortedResult]);
            }

            return acc;
          }, [] as typeahead.ScoredResult[][])
          .map((sectionedSortedResults) =>
            clampSection(sectionedSortedResults, query, grossLimit, config),
          )
          .flat();
      }),
      op.defaultIfEmpty([]),
      tapLogger<typeahead.ScoredResult[]>("postProcessResults"),
    );
  };
}

/**
 * Creates time-based segmentation to prevent UI glitching from too many re-renders
 * TODO: consider using P95-99 latency of slowest emitting search source
 * for defining these values to prevent "jumpy" typeahead results
 */
export function createTimeBasedSegmentation(
  cutoff: Observable<void>,
  end: Observable<void>,
) {
  return (
    source: Observable<typeahead.ScoredResult[]>,
  ): Observable<typeahead.ScoredResult[]> => {
    return source
      .pipe(
        op.connect((shared$) => {
          return rx.merge(
            shared$.pipe(op.takeUntil(cutoff)).pipe(op.auditTime(50)),
            shared$.pipe(op.skipUntil(cutoff)).pipe(op.auditTime(100)),
          );
        }),
      )
      .pipe(op.takeUntil(end));
    // .pipe(tapLogger<typeahead.ScoredResult[]>('createTimeBasedSegmentation'));
  };
}
